import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

import type { ScheduleRouterProps } from './ScheduleRouterPage.types';

/**
 * ScheduleRouterPage - MFE wrapper for Schedule Router view
 *
 * Loads `schedule_mfe/ScheduleRouter` and passes the MSAL instance.
 * The MFE handles its own internal routing.
 *
 * Guard stack (applied in instruct.routes.tsx):
 * 1. AuthenticatedRoute - User must be logged in
 * 2. ModuleGuard (in instruct.routes.tsx) - user must have access to the Instruct module
 *
 * @component
 * @returns {React.ReactElement | null} The ScheduleRouterPage component or null if not authenticated
 *
 * @example
 * // Basic usage
 * <ScheduleRouterPage />
 *
 * @remarks
 * - Uses MSAL for authentication context
 * - Dynamically imports the remote ScheduleRouter component
 * - Handles loading and error states appropriately
 *
 * @throws Will render InlineFeedback error component if MFE loading fails
 *
 * @see docs/ROUTE_ARCHITECTURE.md for detailed documentation
 */

const ScheduleRouterPage = React.memo((): React.ReactElement | null => {
  const { instance } = useMsal();
  const authData = useAuthenticatedUser();
  const { permissions } = usePermissions();
  const getToken = useGetToken();

  const [RemoteRouter, setRemoteRouter] =
    React.useState<React.ComponentType<ScheduleRouterProps> | null>(null);
  const [loadError, setLoadError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const loadRouter = async (): Promise<void> => {
      try {
        const { default: Router } = await import('schedule_mfe/ScheduleRouter');
        setRemoteRouter(() => Router);
      } catch (error) {
        console.error('Error loading ScheduleRouter: ', error);
        setLoadError(error as Error);
      }
    };

    loadRouter();
  }, []);

  if (loadError) {
    return <InlineFeedback type="internal-server-error" showGoHome={false} />;
  }

  if (!RemoteRouter) {
    return <ContentLoader />;
  }

  return (
    <React.Suspense fallback={<ContentLoader />}>
      <RemoteRouter
        msalInstance={instance}
        user={authData}
        permissions={permissions}
        getToken={getToken}
      />
    </React.Suspense>
  );
});

ScheduleRouterPage.displayName = 'ScheduleRouterPage';

export { ScheduleRouterPage };
